import { useEffect, useState } from "react";
import { X, EnvelopeSimple, CheckCircle, CurrencyDollar } from "@phosphor-icons/react";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const METHODS = [
  { value: "cash",   label: "Cash" },
  { value: "check",  label: "Check" },
  { value: "wire",   label: "Wire transfer" },
  { value: "ach",    label: "ACH" },
  { value: "venmo",  label: "Venmo" },
  { value: "zelle",  label: "Zelle" },
  { value: "paypal", label: "PayPal" },
  { value: "other",  label: "Other" },
];

/**
 * Record an offline payment against a project + optionally email a receipt.
 *
 * Two modes:
 *   • Quick "Mark invoice as paid" — fills the outstanding balance, sends receipt.
 *   • Custom partial payment — admin enters amount, method, date, note.
 *
 * `defaultFullPayment` sets the modal to full-balance mode on open (used when the trigger
 * is the explicit "Mark invoice paid" button).
 */
export function RecordPaymentModal({ open, onClose, project, onSaved, defaultFullPayment = false }) {
  const [mode, setMode] = useState("full");          // "full" | "partial"
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [paidAt, setPaidAt] = useState(todayIso());
  const [note, setNote] = useState("");
  const [sendReceipt, setSendReceipt] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(defaultFullPayment ? "full" : "partial");
    setAmount("");
    setMethod("cash");
    setPaidAt(todayIso());
    setNote("");
    setSendReceipt(true);
  }, [open, defaultFullPayment]);

  if (!open || !project) return null;

  const total = Number(project.total_usd || 0);
  const paid = Number(project.paid_amount || 0);
  const balance = Math.max(0, total - paid);

  const enteredAmount = mode === "full" ? balance : Number(amount || 0);
  const newPaid = Math.min(total, paid + enteredAmount);
  const newBalance = Math.max(0, total - newPaid);
  const willBeFullyPaid = newBalance <= 0.01 && total > 0;

  const validate = () => {
    if (mode === "partial") {
      if (!amount || enteredAmount <= 0) return "Amount is required";
      if (enteredAmount > balance + 0.01) return `Amount exceeds outstanding balance (${formatUSD(balance)})`;
    } else if (balance <= 0) {
      return "No outstanding balance";
    }
    if (sendReceipt && !project.client_email) return "Client has no email - turn off receipt or add an email";
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v !== true) return toast.error(v);
    setSaving(true);
    try {
      const body = {
        method,
        paid_at: paidAt || undefined,
        note: note.trim() || undefined,
        send_receipt: sendReceipt,
      };
      if (mode === "full") body.mark_paid_in_full = true;
      else body.amount = enteredAmount;

      const { data } = await api.post(`/admin/projects/${project.id}/record-payment`, body);
      const recMsg = data.receipt_sent
        ? ` & receipt sent to ${data.payment.receipt_sent_to}`
        : (sendReceipt ? ` (receipt failed: ${data.receipt_error || "unknown"})` : "");
      toast.success(`Recorded ${formatUSD(data.payment.amount_usd)} payment${recMsg}`);
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="record-payment-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-[#262626] bg-[#0a0a0a]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Payment</div>
            <div className="font-display text-xl mt-1">Record payment</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-5">
          {/* Current state */}
          <div className="grid grid-cols-3 gap-px bg-[#262626] border border-[#262626]" data-testid="record-current-state">
            <Cell label="Total" value={formatUSD(total)} />
            <Cell label="Paid" value={formatUSD(paid)} />
            <Cell label="Balance" value={formatUSD(balance)} accent={balance > 0} />
          </div>

          {/* Mode tabs */}
          <div className="grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]" role="radiogroup">
            <button
              type="button"
              onClick={() => setMode("full")}
              data-testid="record-mode-full"
              className={`px-3 py-2.5 text-xs uppercase tracking-wider transition-colors ${mode === "full" ? "bg-[#FF2D2D] text-white" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}
            >
              Mark paid in full
            </button>
            <button
              type="button"
              onClick={() => setMode("partial")}
              data-testid="record-mode-partial"
              className={`px-3 py-2.5 text-xs uppercase tracking-wider transition-colors ${mode === "partial" ? "bg-[#FF2D2D] text-white" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}
            >
              Partial payment
            </button>
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-2 gap-3">
            {mode === "partial" && (
              <Field label="Amount" required>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a3a3a3] text-sm">$</span>
                  <input
                    type="number"
                    min="0.01"
                    max={balance}
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={balance.toFixed(2)}
                    data-testid="record-amount-input"
                    className="w-full bg-[#050505] border border-[#262626] pl-7 pr-3 py-2 text-white tabular-nums focus:border-[#FF2D2D] outline-none"
                  />
                </div>
              </Field>
            )}
            <Field label="Method" required>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                data-testid="record-method-select"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
              >
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Paid on">
              <input
                type="date"
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
                data-testid="record-paidat-input"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
              />
            </Field>
          </div>

          <Field label="Note (optional, included in receipt)">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="e.g. Wire received 2025-11-12, ref #ABC123"
              data-testid="record-note-input"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
            />
          </Field>

          {/* Receipt toggle */}
          <label className="flex items-start gap-3 border border-[#262626] bg-[#050505] p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={sendReceipt}
              onChange={(e) => setSendReceipt(e.target.checked)}
              data-testid="record-send-receipt"
              className="accent-[#FF2D2D] mt-0.5"
            />
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-sm">
                <EnvelopeSimple size={14} weight="fill" className="text-[#FF2D2D]" />
                Email receipt to client
              </div>
              <div className="text-[10px] text-[#a3a3a3] mt-0.5">
                {project.client_email || project.client_name || "client"}
              </div>
            </div>
          </label>

          {/* Preview */}
          {enteredAmount > 0 && (
            <div className="border border-[#FF2D2D] bg-[#FF2D2D]/5 p-4 space-y-2" data-testid="record-preview">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">After this payment</div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase text-[#a3a3a3] tracking-wider">Paid</div>
                  <div className="font-display text-lg tabular-nums">{formatUSD(newPaid)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#a3a3a3] tracking-wider">Balance</div>
                  <div className={`font-display text-lg tabular-nums ${newBalance > 0 ? "text-[#FF2D2D]" : "text-[#28C878]"}`}>
                    {formatUSD(newBalance)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#a3a3a3] tracking-wider">Status</div>
                  <div className={`text-sm font-semibold mt-1 ${willBeFullyPaid ? "text-[#28C878]" : ""}`}>
                    {willBeFullyPaid ? "PAID ✓" : (paid + enteredAmount >= Number(project.deposit_usd || 0) ? "Deposit paid" : "Unpaid")}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} className="btn-outline px-4 py-2 text-xs uppercase tracking-wider">Cancel</button>
            <button
              type="submit"
              disabled={saving || enteredAmount <= 0}
              data-testid="record-submit"
              className="btn-primary px-5 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
            >
              {willBeFullyPaid ? <CheckCircle size={12} weight="fill" /> : <CurrencyDollar size={12} weight="bold" />}
              {saving ? "Saving..." : `Record ${formatUSD(enteredAmount)}${sendReceipt ? " + send receipt" : ""}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div className="bg-[#0a0a0a] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3]">{label}</div>
      <div className={`font-display text-lg mt-0.5 tabular-nums ${accent ? "text-[#FF2D2D]" : ""}`}>{value}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
        {label} {required && <span className="text-[#FF2D2D]">*</span>}
      </label>
      {children}
    </div>
  );
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
